import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    PiArrowLeftBold,
    PiArrowsClockwiseBold,
    PiCaretDownBold,
    PiFloppyDiskBold,
    PiHandshakeBold,
    PiImageBold,
    PiMagnifyingGlassBold,
    PiPlusBold,
    PiSealCheckBold,
    PiTrashBold,
    PiXBold,
} from 'react-icons/pi';
import FileDropzone from '@v2/components/ui/FileDropzone';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import toast from 'react-hot-toast';

/* ─── Masks ──────────────────────────────────────────────────── */
function maskCnpj(v: string) {
    const d = v.replace(/\D/g, '').slice(0, 14);
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, (_, a, b, c, e, f) =>
        `${a}.${b}.${c}/${e}` + (f ? `-${f}` : ''));
}
function maskPhone(v: string) {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, (_, a, b, c) => `(${a}) ${b}` + (c ? `-${c}` : ''));
    return d.replace(/(\d{2})(\d{5})(\d{0,4})/, (_, a, b, c) => `(${a}) ${b}` + (c ? `-${c}` : ''));
}
function maskCep(v: string) {
    const d = v.replace(/\D/g, '').slice(0, 8);
    return d.replace(/(\d{5})(\d{0,3})/, (_, a, b) => a + (b ? `-${b}` : ''));
}

/* ─── UI helpers ─────────────────────────────────────────────── */
const inputCls = 'w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none transition focus:border-amber-400 focus:ring-1 focus:ring-amber-400/30 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-200 dark:focus:border-amber-500/60 dark:focus:ring-amber-500/20';

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
    return (
        <div className={cn('flex flex-col gap-1', className)}>
            <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">{label}</label>
            {children}
        </div>
    );
}
function SectionTitle({ children }: { children: React.ReactNode }) {
    return <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">{children}</p>;
}

/* ─── Types ──────────────────────────────────────────────────── */
type FormState = {
    name: string; legal_name: string; cnpj: string;
    email: string; phone: string; address: string; neighborhood: string;
    zip_code: string; city: string; state: string; notes: string; active: boolean;
};
const EMPTY: FormState = {
    name: '', legal_name: '', cnpj: '', email: '', phone: '',
    address: '', neighborhood: '', zip_code: '', city: '', state: '', notes: '', active: true,
};

/* ─── Logo Uploader ──────────────────────────────────────────── */
function LogoUploader({ clientId, currentUrl, pendingFile, onFileChange }: {
    clientId: string | undefined;
    currentUrl: string | null;
    pendingFile: File | null;
    onFileChange: (file: File | null) => void;
}) {
    const [preview, setPreview] = useState<string | null>(currentUrl);
    const [uploading, setUploading] = useState(false);

    useEffect(() => { setPreview(currentUrl); }, [currentUrl]);

    async function handleFile(file: File) {
        if (!file.type.startsWith('image/') || file.size > 2 * 1024 * 1024) {
            toast.error('Use uma imagem PNG, JPG, WEBP ou SVG de até 2 MB.');
            return;
        }
        const objectUrl = URL.createObjectURL(file);
        setPreview(objectUrl);
        if (clientId) {
            setUploading(true);
            const form = new FormData();
            form.append('logo', file);
            try {
                const { data } = await api.post(`/clients/${clientId}/logo`, form, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                setPreview(data.logo_url ?? objectUrl);
                onFileChange(null);
                toast.success('Logo atualizado');
            } catch {
                setPreview(currentUrl);
                onFileChange(null);
                toast.error('Falha ao enviar o logo.');
            } finally { setUploading(false); }
        } else {
            onFileChange(file);
        }
    }

    async function removeLogo() {
        if (clientId) {
            await api.delete(`/clients/${clientId}/logo`).catch(() => {});
            toast.success('Logo removido');
        }
        setPreview(null);
        onFileChange(null);
    }

    return (
        <div className="flex items-start gap-4">
            <div className="relative flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800">
                {preview
                    ? <img src={preview} alt="Logo" className="size-full object-contain p-1" />
                    : <PiImageBold className="size-8 text-gray-300 dark:text-zinc-700" />
                }
                {uploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-zinc-900/70">
                        <PiArrowsClockwiseBold className="size-5 animate-spin text-amber-500" />
                    </div>
                )}
            </div>
            <div className="min-w-0 flex-1 space-y-2">
                <FileDropzone
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    maxSizeMb={2}
                    loading={uploading}
                    disabled={uploading}
                    label={preview ? 'Trocar logo' : 'Arraste o logo aqui'}
                    hint="PNG, JPG, SVG ou WebP · clique para selecionar"
                    showSelected={false}
                    className="[&>label]:py-4"
                    onFiles={(files) => {
                        if (files[0]) void handleFile(files[0]);
                    }}
                    onError={(msg) => toast.error(msg)}
                />
                {(preview || pendingFile) && (
                    <button
                        type="button"
                        onClick={() => void removeLogo()}
                        className="flex items-center gap-2 rounded-xl border border-red-200 px-3 py-1.5 text-xs font-bold text-red-500 transition hover:bg-red-50 dark:border-red-500/30 dark:hover:bg-red-500/10"
                    >
                        <PiTrashBold className="size-3.5" />
                        Remover
                    </button>
                )}
            </div>
        </div>
    );
}

/* ─── Buckler One Toggle ─────────────────────────────────────── */
function BucklerOneToggle({ clientId, active, contractStartedAt, clienteOneSlug }: {
    clientId: string;
    active: boolean;
    contractStartedAt: string | null;
    clienteOneSlug: string | null;
}) {
    const queryClient = useQueryClient();
    const [dateInput, setDateInput] = useState<string>(
        contractStartedAt ?? new Date().toISOString().slice(0, 10),
    );
    const [slugInput, setSlugInput] = useState<string>(clienteOneSlug ?? '');

    useEffect(() => {
        setSlugInput(clienteOneSlug ?? '');
    }, [clienteOneSlug]);

    const { mutate, isPending } = useMutation({
        mutationFn: () =>
            api.post(`/clients/${clientId}/franchisor-network/buckler-one`, {
                active: !active,
                ...(!active && dateInput ? { contract_started_at: dateInput } : {}),
                ...(!active && slugInput.trim() ? { cliente_one_slug: slugInput.trim() } : {}),
            }),
        onSuccess: () => {
            toast.success(active ? 'Buckler One desativado' : 'Buckler One habilitado!');
            queryClient.invalidateQueries({ queryKey: ['franchisors'] });
            queryClient.invalidateQueries({ queryKey: ['franchisor-edit', clientId] });
        },
        onError: (err: unknown) => {
            const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
            toast.error(msg || 'Não foi possível alterar o Buckler One');
        },
    });

    return (
        <div className={cn(
            'rounded-2xl border',
            active
                ? 'border-amber-200 bg-amber-50/60 dark:border-amber-500/20 dark:bg-amber-500/5'
                : 'border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900',
        )}>
            <div className="flex items-center justify-between gap-4 p-5">
                <div className="flex items-center gap-3">
                    <div className={cn(
                        'flex size-10 shrink-0 items-center justify-center rounded-xl',
                        active ? 'bg-amber-100 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400' : 'bg-gray-100 text-gray-400 dark:bg-zinc-800 dark:text-zinc-500',
                    )}>
                        <PiSealCheckBold className="size-5" />
                    </div>
                    <div>
                        <p className="text-sm font-black text-gray-900 dark:text-zinc-100">Buckler One</p>
                        <p className="text-xs text-gray-500 dark:text-zinc-500">
                            {active ? 'Parceria ativa — rede participante do programa' : 'Rede não participa do Buckler One'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {active && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-100 px-2.5 py-0.5 text-[11px] font-black text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/20 dark:text-amber-400">
                            <PiHandshakeBold className="size-3" />
                            Ativo
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={() => mutate()}
                        disabled={isPending}
                        className={cn(
                            'relative shrink-0 rounded-full transition-colors duration-200 disabled:opacity-60',
                            active ? 'bg-amber-500' : 'bg-zinc-300 dark:bg-zinc-700',
                        )}
                        style={{ width: 44, height: 24 }}
                    >
                        {isPending
                            ? <PiArrowsClockwiseBold className="absolute inset-0 m-auto size-3.5 animate-spin text-white" />
                            : <span className="absolute top-[2px] size-5 rounded-full bg-white shadow-md transition-all duration-200" style={{ left: active ? 22 : 2 }} />
                        }
                    </button>
                </div>
            </div>

            {/* Data + slug Cliente One — editáveis quando ativo ou ao ativar */}
            <div className={cn(
                'space-y-4 border-t px-5 pb-5 pt-4',
                active ? 'border-amber-200/60 dark:border-amber-500/15' : 'border-gray-100 dark:border-zinc-800',
            )}>
                <div>
                    <label className="mb-1.5 block text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">
                        Início do contrato
                    </label>
                    <input
                        type="date"
                        value={dateInput}
                        onChange={(e) => setDateInput(e.target.value)}
                        className={cn(inputCls, 'max-w-[180px]')}
                    />
                    <p className="mt-1.5 text-[11px] text-gray-400 dark:text-zinc-600">
                        Deals ganhos a partir desta data serão contabilizados no Buckler One.
                    </p>
                </div>

                <div>
                    <label className="mb-1.5 block text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-zinc-600">
                        Slug Cliente One (Ploomes)
                    </label>
                    <input
                        type="text"
                        value={slugInput}
                        onChange={(e) => setSlugInput(e.target.value)}
                        placeholder="Ex.: Cariani"
                        className={cn(inputCls, 'max-w-md')}
                    />
                    <p className="mt-1.5 text-[11px] text-gray-400 dark:text-zinc-600">
                        Deve bater com o Options «Cliente One» do contato no Ploomes. Todas as vendas com esse slug ficam vinculadas a esta franqueadora.
                    </p>
                </div>

                {active && (dateInput !== contractStartedAt || slugInput.trim() !== (clienteOneSlug ?? '')) && (
                    <button
                        type="button"
                        disabled={isPending}
                        onClick={() =>
                            api.post(`/clients/${clientId}/franchisor-network/buckler-one`, {
                                active: true,
                                contract_started_at: dateInput,
                                cliente_one_slug: slugInput.trim(),
                            }).then(() => {
                                toast.success('Buckler One atualizado');
                                queryClient.invalidateQueries({ queryKey: ['franchisors'] });
                                queryClient.invalidateQueries({ queryKey: ['franchisor-edit', clientId] });
                            }).catch((err: unknown) => {
                                const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
                                toast.error(msg || 'Erro ao salvar');
                            })
                        }
                        className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 transition hover:bg-amber-100 disabled:opacity-50 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400 dark:hover:bg-amber-500/20"
                    >
                        <PiFloppyDiskBold className="size-3.5" />
                        Salvar Buckler One
                    </button>
                )}
            </div>
        </div>
    );
}

/* ─── Page ───────────────────────────────────────────────────── */
export default function FranqueadoraFormPage() {
    const { id } = useParams<{ id: string }>();
    const isEdit = !!id;
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [form, setForm] = useState<FormState>(EMPTY);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [currentLogoUrl, setCurrentLogoUrl] = useState<string | null>(null);
    const [bucklerOneActive, setBucklerOneActive] = useState(false);
    const [bucklerOneContractDate, setBucklerOneContractDate] = useState<string | null>(null);
    const [bucklerOneSlug, setBucklerOneSlug] = useState<string | null>(null);

    /* Carrega dados da franqueadora em modo edição */
    const { data: clientData, isLoading: loadingClient } = useQuery({
        queryKey: ['franchisor-edit', id],
        queryFn: async () => {
            const [clientRes, networkRes] = await Promise.all([
                api.get(`/clients/${id}`),
                api.get(`/clients/${id}/franchisor-network`),
            ]);
            return {
                client: clientRes.data.data as Record<string, unknown>,
                buckler_one: networkRes.data.data?.buckler_one as {
                    active: boolean;
                    contract_started_at?: string;
                    cliente_one_slug?: string | null;
                } | null,
            };
        },
        enabled: isEdit,
    });

    useEffect(() => {
        if (!clientData) return;
        const d = clientData.client;
        setForm({
            name:         String(d.name ?? ''),
            legal_name:   String(d.legal_name ?? ''),
            cnpj:         maskCnpj(String(d.cnpj ?? '')),
            email:        String(d.email ?? ''),
            phone:        maskPhone(String(d.phone ?? '')),
            address:      String(d.address ?? ''),
            neighborhood: String(d.neighborhood ?? ''),
            zip_code:     maskCep(String(d.zip_code ?? '')),
            city:         String(d.city ?? ''),
            state:        String(d.state ?? ''),
            notes:        String(d.notes ?? ''),
            active:       Boolean(d.active ?? true),
        });
        setCurrentLogoUrl(d.logo_url as string | null ?? null);
        setBucklerOneActive(clientData.buckler_one?.active === true);
        setBucklerOneContractDate(clientData.buckler_one?.contract_started_at ?? null);
        setBucklerOneSlug(clientData.buckler_one?.cliente_one_slug ?? null);
    }, [clientData]);

    /* Mutação de salvar */
    const { mutate: save, isPending: saving } = useMutation({
        mutationFn: async () => {
            const payload = {
                ...form,
                cnpj:     form.cnpj.replace(/\D/g, '') || null,
                phone:    form.phone.replace(/\D/g, '') || null,
                zip_code: form.zip_code.replace(/\D/g, '') || null,
            };
            if (isEdit) {
                return (await api.put(`/clients/${id}`, payload)).data;
            }
            return (await api.post('/clients', payload)).data;
        },
        onSuccess: async (body) => {
            queryClient.invalidateQueries({ queryKey: ['franchisors'] });
            queryClient.invalidateQueries({ queryKey: ['clients-stats'] });
            if (isEdit) {
                queryClient.invalidateQueries({ queryKey: ['franchisor-edit', id] });
                navigate(`/franchisors`);
            } else {
                const newId = body?.data?.id ?? body?.id;
                if (newId && logoFile) {
                    const fd = new FormData();
                    fd.append('logo', logoFile);
                    await api.post(`/clients/${newId}/logo`, fd, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                    }).catch(() => {});
                }
                navigate('/franchisors');
            }
            toast.success(isEdit ? 'Franqueadora atualizada' : 'Franqueadora criada');
        },
        onError: (err: unknown) => {
            const resp = (err as { response?: { data?: { errors?: Record<string, string[]> } } }).response?.data;
            if (resp?.errors) {
                const flat: Record<string, string> = {};
                for (const [k, v] of Object.entries(resp.errors)) flat[k] = v[0];
                setErrors(flat);
            }
            toast.error('Não foi possível salvar a franqueadora');
        },
    });

    function set(key: keyof FormState, v: string | boolean) {
        setForm((f) => ({ ...f, [key]: v }));
        setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
    }

    const clientName = isEdit ? (clientData?.client.name as string | undefined) : undefined;

    return (
        <div className="space-y-6 px-4 py-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-zinc-600">
                <Link to="/franchisors" className="hover:text-amber-500 dark:hover:text-amber-400">Franqueadoras</Link>
                {isEdit && clientName && (
                    <>
                        <span>•</span>
                        <span className="max-w-[200px] truncate text-gray-600 dark:text-zinc-400">{clientName}</span>
                    </>
                )}
                <span>•</span>
                <span className="text-gray-600 dark:text-zinc-400">{isEdit ? 'Editar' : 'Nova franqueadora'}</span>
            </nav>

            {/* Header */}
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-black text-gray-900 dark:text-white">
                        {isEdit ? 'Editar franqueadora' : 'Nova franqueadora'}
                    </h1>
                    {isEdit && clientName && (
                        <p className="mt-0.5 text-sm text-gray-400 dark:text-zinc-500">{clientName}</p>
                    )}
                </div>
                <Link
                    to="/franchisors"
                    className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-500 transition hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
                >
                    <PiArrowLeftBold className="size-3.5" />
                    Voltar
                </Link>
            </div>

            {/* Form */}
            {isEdit && loadingClient ? (
                <div className="flex h-48 items-center justify-center rounded-2xl border border-gray-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
                    <PiArrowsClockwiseBold className="size-6 animate-spin text-gray-300 dark:text-zinc-700" />
                </div>
            ) : (
                <div className="space-y-6 rounded-2xl border border-gray-200 bg-white p-6 dark:border-zinc-800 dark:bg-zinc-900">

                    {/* Logo */}
                    <section>
                        <SectionTitle>Logo</SectionTitle>
                        <LogoUploader
                            clientId={id}
                            currentUrl={currentLogoUrl}
                            pendingFile={logoFile}
                            onFileChange={setLogoFile}
                        />
                    </section>

                    <hr className="border-gray-100 dark:border-zinc-800" />

                    {/* Identificação */}
                    <section>
                        <SectionTitle>Identificação</SectionTitle>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            <Field label="Nome fantasia *" className="sm:col-span-2 lg:col-span-4">
                                <input className={inputCls} value={form.name} onChange={(e) => set('name', e.target.value)} />
                                {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                            </Field>
                            <Field label="Razão social" className="sm:col-span-2 lg:col-span-4">
                                <input className={inputCls} value={form.legal_name} onChange={(e) => set('legal_name', e.target.value)} />
                            </Field>
                            <Field label="CNPJ">
                                <input className={cn(inputCls, 'font-mono')} inputMode="numeric" value={form.cnpj} onChange={(e) => set('cnpj', maskCnpj(e.target.value))} placeholder="00.000.000/0000-00" />
                            </Field>
                            <Field label="Telefone">
                                <input className={cn(inputCls, 'font-mono')} inputMode="numeric" value={form.phone} onChange={(e) => set('phone', maskPhone(e.target.value))} placeholder="(00) 00000-0000" />
                            </Field>
                            <Field label="E-mail" className="sm:col-span-2">
                                <input className={inputCls} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
                                {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                            </Field>
                        </div>
                    </section>

                    <hr className="border-gray-100 dark:border-zinc-800" />

                    {/* Endereço */}
                    <section>
                        <SectionTitle>Endereço</SectionTitle>
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                            <Field label="Logradouro" className="sm:col-span-2 lg:col-span-2">
                                <input className={inputCls} value={form.address} onChange={(e) => set('address', e.target.value)} />
                            </Field>
                            <Field label="Bairro">
                                <input className={inputCls} value={form.neighborhood} onChange={(e) => set('neighborhood', e.target.value)} />
                            </Field>
                            <Field label="CEP">
                                <input className={cn(inputCls, 'font-mono')} inputMode="numeric" value={form.zip_code} onChange={(e) => set('zip_code', maskCep(e.target.value))} placeholder="00000-000" />
                            </Field>
                            <Field label="Cidade">
                                <input className={inputCls} value={form.city} onChange={(e) => set('city', e.target.value)} />
                            </Field>
                            <Field label="UF">
                                <input className={cn(inputCls, 'uppercase')} maxLength={2} value={form.state} onChange={(e) => set('state', e.target.value.toUpperCase())} placeholder="SP" />
                            </Field>
                        </div>
                    </section>

                    <hr className="border-gray-100 dark:border-zinc-800" />

                    {/* Buckler One — só aparece na edição */}
                    {isEdit && (
                        <>
                            <section>
                                <SectionTitle>Buckler One</SectionTitle>
                                <BucklerOneToggle
                                    clientId={id!}
                                    active={bucklerOneActive}
                                    contractStartedAt={bucklerOneContractDate}
                                    clienteOneSlug={bucklerOneSlug}
                                />
                            </section>
                            <hr className="border-gray-100 dark:border-zinc-800" />
                        </>
                    )}

                    {/* Status + Observações */}
                    <section>
                        <SectionTitle>Status</SectionTitle>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => set('active', !form.active)}
                                className={cn('relative shrink-0 rounded-full transition-colors duration-200', form.active ? 'bg-emerald-500' : 'bg-zinc-300 dark:bg-zinc-700')}
                                style={{ width: 44, height: 24 }}
                            >
                                <span className="absolute top-[2px] size-5 rounded-full bg-white shadow-md transition-all duration-200" style={{ left: form.active ? 22 : 2 }} />
                            </button>
                            <span className="text-sm text-gray-600 dark:text-zinc-400">{form.active ? 'Ativa' : 'Inativa'}</span>
                        </div>
                    </section>

                    <hr className="border-gray-100 dark:border-zinc-800" />

                    <section>
                        <SectionTitle>Observações</SectionTitle>
                        <textarea
                            className={cn(inputCls, 'min-h-[80px] resize-y')}
                            value={form.notes}
                            onChange={(e) => set('notes', e.target.value)}
                            placeholder="Notas internas sobre esta franqueadora…"
                        />
                    </section>
                </div>
            )}

            {/* Footer actions */}
            <div className="flex items-center justify-end gap-3">
                <Link
                    to="/franchisors"
                    className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-500 transition hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800"
                >
                    Cancelar
                </Link>
                <button
                    type="button"
                    onClick={() => save()}
                    disabled={saving || (isEdit && loadingClient)}
                    className="flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-2 text-sm font-black text-white transition hover:bg-amber-600 disabled:opacity-60 dark:bg-[#C9A84C] dark:text-zinc-900 dark:hover:bg-amber-500"
                >
                    {saving
                        ? <PiArrowsClockwiseBold className="size-4 animate-spin" />
                        : isEdit ? <PiFloppyDiskBold className="size-4" /> : <PiPlusBold className="size-4" />
                    }
                    {saving ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Criar franqueadora'}
                </button>
            </div>
        </div>
    );
}
